import { useEffect, useRef, useState } from 'react';

export function useDoubleHorizontalScroll<
  TTop extends HTMLElement = HTMLDivElement,
  TContent extends HTMLElement = HTMLDivElement
>(dependencies: unknown[] = []) {
  const topScrollRef = useRef<TTop>(null);
  const contentScrollRef = useRef<TContent>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const isSyncingTop = useRef(false);
  const isSyncingContent = useRef(false);

  const handleTopScroll = () => {
    if (!topScrollRef.current || !contentScrollRef.current) return;
    if (isSyncingContent.current) {
      isSyncingContent.current = false;
      return;
    }
    isSyncingTop.current = true;
    contentScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  };

  const handleContentScroll = () => {
    if (!topScrollRef.current || !contentScrollRef.current) return;
    if (isSyncingTop.current) {
      isSyncingTop.current = false;
      return;
    }
    isSyncingContent.current = true;
    topScrollRef.current.scrollLeft = contentScrollRef.current.scrollLeft;
  };

  useEffect(() => {
    let rafId: number;
    const updateWidth = () => {
      if (contentScrollRef.current) {
        setContentWidth(contentScrollRef.current.scrollWidth);
      }
    };

    const scheduledUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateWidth);
    };

    scheduledUpdate();
    // Schedule another update shortly after mount to catch late layouts
    const timeoutId = setTimeout(scheduledUpdate, 100);

    const element = contentScrollRef.current;
    if (!element) return;

    const observer = new ResizeObserver(() => {
      scheduledUpdate();
    });

    observer.observe(element);
    Array.from(element.children).forEach((child) => {
      observer.observe(child);
    });

    const mutationObserver = new MutationObserver(() => {
      scheduledUpdate();
      // Re-observe new children
      Array.from(element.children).forEach((child) => {
        observer.observe(child);
      });
    });

    mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });

    window.addEventListener('resize', scheduledUpdate);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', scheduledUpdate);
    };
  }, dependencies);

  return {
    topScrollRef,
    contentScrollRef,
    contentWidth,
    handleTopScroll,
    handleContentScroll,
  };
}
